import invariant from 'invariant';
import { useMemo } from 'react';

import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { HeadingSmall } from '#/components/Text/HeadingSmall';
import { RichText } from '#/components/Text/RichText';
import { RichTextHeadline } from '#/components/Text/RichTextHeadline';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiLink } from '#/modules/cms/api/types/CmsApiLink';
import type { CmsApiSplitBlockAppearance } from '#/modules/cms/api/types/blocks/CmsApiSplitContentBlock';
import { colors, gutterWidth } from '#/theme/theme';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { showModal } from '#/zustand-stores/modalStore';
import { setPreSignupJoinedMovement } from '#/zustand-stores/preSignupJoinMovementStore';

import {
  CmsSplitContentBlockMovementCounterDigit,
  CmsSplitContentBlockMovementCounterDigitContainer,
  CmsSplitContentBlockMovementCounterDigitSlot,
  CmsSplitContentBlockMovementPeopleJoined,
} from './CmsSplitContentBlock.styled';
import { CmsButton } from '#/modules/cms/components/Button/CmsButton';

type Props = {
  label: string | null;
  headline: string | null;
  html: string;
  button: CmsApiLink | null; // Used by TEXT
  hideButton: boolean; // Used by TEXT
  appearance: CmsApiSplitBlockAppearance;
  index: number;
  blockId: string;
  blockIndex: number;
  pageUpdated: string;
  showTextBox: boolean;
  showBackgroundImage: boolean; // Used by TEXT
  movementSignupButtonText?: string;
  movementUserLoggedIn?: boolean;
  movementUserSignedUp?: boolean;
  movementSignUp?: () => void;
  movementCount?: number;
  movementSlug?: string;
};

const digitsSlot = (number: number) => (
  <CmsSplitContentBlockMovementCounterDigitSlot $number={number}>
    {Array.from({ length: 10 }, (_, digit) => (
      <CmsSplitContentBlockMovementCounterDigit key={digit}>
        {digit}
      </CmsSplitContentBlockMovementCounterDigit>
    ))}
  </CmsSplitContentBlockMovementCounterDigitSlot>
);

export function CmsSplitContentTextContentInner({
  appearance,
  blockId,
  blockIndex,
  index,
  pageUpdated,
  label,
  headline,
  html,
  button,
  hideButton,
  showTextBox,
  showBackgroundImage,
  movementSignupButtonText,
  movementUserLoggedIn,
  movementUserSignedUp,
  movementSignUp,
  movementCount,
  movementSlug,
}: Props) {
  const { i18n } = useI18n();

  const boxShadow = useMemo(() => {
    if (showTextBox) {
      return [null, null, '0 0 6px rgba(0,0,0,0.3)'];
    }

    return undefined;
  }, [showTextBox]);

  const joinMovement = () => {
    trackClicked('CMS Block', {
      block_position: blockIndex,
      block_type: 'Split Content Movement',
      page_updated: pageUpdated,
    });

    if (movementUserLoggedIn) {
      invariant(movementSignUp, 'Missing movementSignUp function');
      movementSignUp();
    } else {
      invariant(movementSlug, 'Movement slug better not be null');
      showModal('SIGNUP', {
        title: 'Join our movement',
        onSignup: () => {
          setPreSignupJoinedMovement(movementSlug);
        },
        onLogin: () => {
          setPreSignupJoinedMovement(movementSlug);
        },
      });
    }
  };

  let digits;
  let digitItems: { digit: string; key: string }[] | undefined;
  if (movementCount !== undefined) {
    digits = movementCount.toString().split('');
    while (digits.length < 4) {
      digits.unshift('0');
    }
    digitItems = digits.map((digit, position) => ({
      digit,
      key: `${movementCount}-${position}`,
    }));
  }

  return (
    <Box
      height="100%"
      width="100%"
      bg={colors.white}
      boxShadow={boxShadow}
      borderRadius={showTextBox ? 12 : 24}
    >
      <Box
        position="relative"
        px={[showBackgroundImage ? gutterWidth / 2 : null, null, gutterWidth]}
        pr={showTextBox ? gutterWidth / 3 : [null, null, 0]}
        py={[
          showBackgroundImage ? gutterWidth / 2 : null,
          null,
          ['OVERLAPPING', 'FULL_WIDTH'].includes(appearance) ? gutterWidth : 0,
        ]}
        height="100%"
      >
        {label && (
          <HeadingSmall color={colors.lightContentGrey}>{label}</HeadingSmall>
        )}
        {headline && (
          <Box
            display="block"
            mb={24}
            color={colors.mediumContentGrey}
            textDecoration="none"
          >
            <RichTextHeadline>{headline}</RichTextHeadline>
          </Box>
        )}
        <RichText
          analyticsData={{
            eventName: 'CMS Block',
            eventProperties: {
              block_position: blockIndex,
              block_type: 'Split Content',
            },
          }}
          dangerouslySetInnerHTML={{
            __html: html,
          }}
        />
        {button && !hideButton && (
          <Box mt={[24, null, 36]}>
            <CmsButton
              buttonData={button}
              eventProperties={{
                block_position: blockIndex,
                block_type: 'Split Content',
                button_text: button.text,
                page_updated: pageUpdated,
              }}
              blockId={blockId}
              sectionIndex={index}
              render={({ data, children, title, qaId }) => (
                <Button data={data} title={title} variant="primary" qaId={qaId}>
                  {children}
                </Button>
              )}
            />
          </Box>
        )}
        {movementSignupButtonText && digitItems && (
          <>
            <Box display="flex" alignItems="center" mt="30px" flexWrap="wrap">
              <Box mr="16px">
                {digitItems.map((item) => (
                  <CmsSplitContentBlockMovementCounterDigitContainer
                    key={item.key}
                    data-qa-id="counter-digit"
                    data-qa-value={item.digit}
                  >
                    {digitsSlot(parseInt(item.digit, 10))}
                  </CmsSplitContentBlockMovementCounterDigitContainer>
                ))}
              </Box>
              <CmsSplitContentBlockMovementPeopleJoined>
                {i18n('people have joined')}
              </CmsSplitContentBlockMovementPeopleJoined>
            </Box>
            {!movementUserSignedUp && (
              <Box mt="30px">
                <Button
                  variant="primary"
                  type="button"
                  onClick={joinMovement}
                  qaId="join-movement-button"
                >
                  {movementSignupButtonText}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
